import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Heading, VStack, HStack, Input, Select, Button, Text, Flex, Progress, useToast, Accordion, AccordionItem, AccordionButton, AccordionIcon, AccordionPanel, Spinner, Tag, Icon, SimpleGrid, Badge, Collapse, Code, useDisclosure, Tooltip, Spacer, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
    Skeleton, SkeletonCircle, ButtonGroup, Image
} from '@chakra-ui/react';
import { FaDownload, FaPlay, FaStop, FaBoxes } from 'react-icons/fa';
import { FiRefreshCw, FiClock, FiCheckCircle, FiAlertCircle, FiDollarSign, FiPower, FiChevronsDown, FiChevronsUp, FiShoppingCart, FiFileText, FiXCircle } from 'react-icons/fi';
import {
    getSincronizacaoStatus,
    getSincronizacaoLogs,
    prepararAtualizacao,
    executarAtualizacao,
    pararAtualizacao,
    solicitarExportacaoRevisao,
    solicitarExportacaoResultado,
    baixarArquivoExportado,
} from "../services/api";
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const CopyableText = ({ text, label }) => {
    const toast = useToast();
    const handleCopy = (e) => {
        e.stopPropagation();
        navigator.clipboard.writeText(text);
        toast({
            title: `"${label}" copiado!`,
            description: text,
            status: "success",
            duration: 2000,
            isClosable: true,
            position: "top"
        });
    };
    return (
        <Tooltip label={`Clique para copiar: ${text}`} placement="top" hasArrow>
            <Text
                fontSize="sm"
                onClick={handleCopy}
                cursor="pointer"
                _hover={{ color: 'blue.500' }}
                noOfLines={1}
                title={text}
                p={1}
                borderRadius="md"
            >
                <Box as="span" fontWeight="bold">{label}:</Box>
                <Box as="span" ml={1} fontWeight="normal">{text}</Box>
            </Text>
        </Tooltip>
    );
};

const CampoIcon = ({ campo }) => {
    const iconMap = { quantidade: FaBoxes, valor_venda: FiDollarSign, valor_compra: FiShoppingCart, status: FiPower };
    const colorMap = { quantidade: 'orange.500', valor_venda: 'green.500', valor_compra: 'blue.500', status: 'purple.500' };
    return <Icon as={iconMap[campo] || FaBoxes} color={colorMap[campo] || 'gray.500'} title={`Campo: ${campo}`} />;
};

const SincronizacaoItem = ({ sinc }) => {
    const { isOpen, onToggle } = useDisclosure();
    const formatJsonPayload = (payload) => {
        if (!payload) return null;
        try {
            const data = typeof payload === 'string' ? JSON.parse(payload) : payload;
            return JSON.stringify(data, null, 2);
        } catch (e) { return String(payload); }
    };
    return (
        <Box borderWidth="1px" borderRadius="md" bg="gray.50" overflow="hidden">
            <Flex onClick={onToggle} cursor="pointer" p={4} alignItems="center" _hover={{ bg: 'gray.100' }}>
                <SimpleGrid columns={{ base: 2, md: 4 }} spacing={4} flex="1">
                    <Text fontSize="sm"><b>Marketplace:</b> {sinc.marketplace}</Text>
                    <Text fontSize="sm"><b>Conta:</b> {sinc.conta}</Text>
                    <Tag size="sm" w="fit-content" colorScheme={sinc.status === 'SUCESSO' ? 'green' : 'red'}>{sinc.status}</Tag>
                    <Text fontSize="sm"><b>HTTP:</b> {sinc.http_code}</Text>
                </SimpleGrid>
                <Icon as={isOpen ? FiChevronsUp : FiChevronsDown} ml={4} />
            </Flex>
            <Collapse in={isOpen} animateOpacity>
                <Box p={4} borderTopWidth="1px">
                    <Box p={3} bg="blue.50" borderRadius="md" mb={4}><Text fontSize="sm"><b>Mensagem:</b> {sinc.mensagem_retorno}</Text></Box>
                    <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        {sinc.payload_enviado && sinc.payload_enviado !== 'null' && <Box><Text fontWeight="bold" mb={2}>Payload Enviado</Text><Code w="100%" p={3} borderRadius="md" display="block" whiteSpace="pre-wrap">{formatJsonPayload(sinc.payload_enviado)}</Code></Box>}
                        {sinc.payload_recebido && sinc.payload_recebido !== 'null' && <Box><Text fontWeight="bold" mb={2}>Payload Recebido</Text><Code w="100%" p={3} borderRadius="md" display="block" whiteSpace="pre-wrap">{formatJsonPayload(sinc.payload_recebido)}</Code></Box>}
                    </SimpleGrid>
                </Box>
            </Collapse>
        </Box>
    );
};

const ControleAtualizacaoSkeleton = () => {
    return (
        <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
            <VStack spacing={5} align="stretch">
                <HStack spacing={4} align="center">
                    <SkeletonCircle size="8" />
                    <VStack align="start" spacing={2} flex="1">
                        <Skeleton height="20px" width="200px" />
                        <Skeleton height="14px" width="250px" />
                    </VStack>
                </HStack>
                <Skeleton height="10px" />
                <Skeleton height="12px" width="150px" />
                <HStack spacing={4} justify="center" pt={2}>
                    <Skeleton height="40px" width="150px" />
                </HStack>
            </VStack>
        </Box>
    );
};

const ControleAtualizacaoCard = ({ jobStatus, onAction, exportLoading, supplier }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [isStopLoading, setIsStopLoading] = useState(false);
    const [isAlertOpen, setIsAlertOpen] = useState(false);
    const cancelRef = useRef();
    const toast = useToast();

    const handleAction = async (action) => {
        setIsLoading(true);
        try {
            const response = await onAction('processo', action, supplier);
            toast({
                title: response.mensagem || 'Ação disparada com sucesso!',
                status: 'success',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Erro ao executar ação.',
                description: error.response?.data?.mensagem || error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleStop = async () => {
        setIsStopLoading(true);
        try {
            const response = await onAction('processo', 'parar', supplier);
            toast({
                title: response.mensagem || 'Processo de parada iniciado.',
                status: 'warning',
                duration: 3000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: 'Erro ao parar o processo.',
                description: error.response?.data?.mensagem || error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        } finally {
            setIsStopLoading(false);
            setIsAlertOpen(false);
        }
    }

    const handleExport = async (tipo) => {
        try {
            if (jobStatus[`${tipo}_export_status`] === 'ready') {
                await onAction('baixar', tipo, supplier);
                toast({ title: `Download de ${tipo} iniciado`, status: 'success', duration: 3000 });
            } else {
                const response = await onAction('solicitar', tipo, supplier);
                toast({ title: response.mensagem || `Solicitação de exportação de ${tipo} enviada!`, status: 'info', duration: 4000 });
            }
        } catch (error) {
            toast({
                title: `Erro na exportação de ${tipo}`,
                description: error.message,
                status: 'error',
                duration: 5000,
                isClosable: true,
            });
        }
    };

    const progress = jobStatus.total_items > 0 ? (jobStatus.processed_items / jobStatus.total_items) * 100 : 0;
    const endTime = jobStatus.end_time ? format(parseISO(jobStatus.end_time), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR }) : null;

    const getStatusInfo = () => {
        switch (jobStatus.status) {
            case 'running': return { icon: FiRefreshCw, color: 'blue.500', title: 'Processo em Andamento' };
            case 'completed': return { icon: FiCheckCircle, color: 'green.500', title: 'Processo Concluído' };
            case 'failed': return { icon: FiAlertCircle, color: 'red.500', title: 'Processo Falhou' };
            case 'review_ready': return { icon: FiFileText, color: 'purple.500', title: 'Revisão Pronta para Iniciar' };
            case 'stopped': return { icon: FiAlertCircle, color: 'orange.500', title: 'Processo Interrompido' };
            default: return { icon: FiClock, color: 'gray.500', title: 'Aguardando Ação' };
        }
    };
    const { icon, color, title } = getStatusInfo();

    const renderExportButton = (tipo, label, colorScheme, variant = 'solid') => {
        const status = jobStatus[`${tipo}_export_status`];
        const isLoading = exportLoading[`isExporting${tipo.charAt(0).toUpperCase() + tipo.slice(1)}`];

        let buttonText = `Exportar ${label}`;
        let buttonAction = () => handleExport(tipo);
        let isDisabled = false;
        let finalColorScheme = colorScheme;

        switch (status) {
            case 'pending':
            case 'generating':
                buttonText = `Gerando ${label}...`;
                isDisabled = true;
                break;
            case 'ready':
                buttonText = `Baixar ${label}`;
                finalColorScheme = 'green';
                break;
            case 'failed':
                buttonText = `Falha ao Exportar ${label}`;
                finalColorScheme = 'red';
                break;
            default:
                break;
        }

        return (
            <Button
                leftIcon={<FaDownload />}
                colorScheme={finalColorScheme}
                onClick={buttonAction}
                isLoading={isLoading}
                isDisabled={isDisabled}
                w={{ base: 'full', md: 'auto' }}
                variant={variant}
            >
                {buttonText}
            </Button>
        );
    };

    return (
        <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
            <VStack spacing={4} align="stretch">
                <HStack spacing={4} align="center">
                    <Icon as={icon} w={8} h={8} color={color} animation={jobStatus.status === 'running' ? `spin 2s linear infinite` : 'none'} />
                    <VStack align="start" spacing={0} flex="1">
                        <Heading size="md">{title} - {supplier.toUpperCase()}</Heading>
                        <Text fontSize="sm" color="gray.600">{jobStatus.mensagem}</Text>
                    </VStack>
                </HStack>

                {jobStatus.status === 'running' && (
                    <Box>
                        <HStack justify="space-between">
                            <Text fontSize="sm" fontWeight="medium">Progresso:</Text>
                            <Text fontSize="sm" fontWeight="bold" color="blue.600">{jobStatus.processed_items || 0} / {jobStatus.total_items || 0}</Text>
                        </HStack>
                        <Progress value={progress} size="md" colorScheme="blue" borderRadius="md" mt={1} isIndeterminate={progress === 0 && jobStatus.processed_items > 0} />
                    </Box>
                )}

                {endTime && <Text fontSize="xs" color="gray.500" textAlign="center">
                    {jobStatus.status === 'review_ready' ? 'Revisão preparada em: ' : 'Última execução: '}
                    {endTime}
                </Text>}

                {/* <Flex  direction={{ base: 'column', md: 'row' }} justify="center" w="full" gap={3} pt={2} flexWrap="wrap">
                    {['idle', 'completed', 'failed', 'stopped'].includes(jobStatus.status) && (
                        <Button leftIcon={<FaPlay />} colorScheme="blue" onClick={() => handleAction('preparar')} isLoading={isLoading} w={{ base: 'full', md: 'auto' }}>
                            Iniciar Preparação
                        </Button>
                    )}
                    {jobStatus.status === 'review_ready' && (
                        <Button leftIcon={<FiRefreshCw />} colorScheme="green" onClick={() => handleAction('executar')} isLoading={isLoading} w={{ base: 'full', md: 'auto' }}>
                            Iniciar Atualização
                        </Button>
                    )}
                    {jobStatus.status === 'running' && (
                        <Button leftIcon={<FaStop />} colorScheme="red" onClick={() => setIsAlertOpen(true)} isLoading={isStopLoading} w={{ base: 'full', md: 'auto' }}>
                            Parar Atualização
                        </Button>
                    )}
                    {['review_ready', 'completed', 'failed', 'stopped'].includes(jobStatus.status) && renderExportButton('revisao', 'Revisão', 'gray')}
                    {['completed', 'stopped'].includes(jobStatus.status) && renderExportButton('resultado', 'Resultado', 'purple', 'outline')}
                </Flex> */}
            </VStack>
            <AlertDialog isOpen={isAlertOpen} leastDestructiveRef={cancelRef} onClose={() => setIsAlertOpen(false)}>
                <AlertDialogOverlay><AlertDialogContent>
                    <AlertDialogHeader fontSize="lg" fontWeight="bold">Parar Atualização</AlertDialogHeader>
                    <AlertDialogBody>Tem certeza que deseja interromper o processo? Esta ação não pode ser desfeita.</AlertDialogBody>
                    <AlertDialogFooter>
                        <Button ref={cancelRef} onClick={() => setIsAlertOpen(false)}>Cancelar</Button>
                        <Button colorScheme="red" onClick={handleStop} ml={3} isLoading={isStopLoading}>Parar</Button>
                    </AlertDialogFooter>
                </AlertDialogContent></AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

const LogItem = ({ log, getFornecedor, formatCurrency }) => {
    const isPriceField = log.campo.includes('valor');
    return (
        <AccordionItem>
            <AccordionButton _expanded={{ bg: 'blue.50' }} py={4}>
                <Box flex='1' overflowX={{ base: "auto", md: "hidden" }} >
                    <HStack spacing={4} minWidth="800px">
                        <CampoIcon campo={log.campo} />
                        <CopyableText text={log.produto_id} label="SKU" />
                        <CopyableText text={log.codigo_fornecedor} label="Cód. Forn" />
                        <CopyableText text={log.codigo_global} label="Cód. Global" />
                        <Spacer />
                        <Badge colorScheme="teal" variant="outline" alignSelf="center">{getFornecedor(log)}</Badge>
                        <Tag size="sm" colorScheme={log.campo === 'valor_compra' ? 'blue' : isPriceField ? 'green' : log.campo === 'quantidade' ? 'orange' : 'purple'}>{log.campo.replace('_', ' ').toUpperCase()}</Tag>
                        <Text fontSize="xs" color="gray.500" minW="85px" textAlign="right">{format(parseISO(log.created_at), "dd/MM/yy HH:mm", { locale: ptBR })}</Text>
                    </HStack>
                </Box>
                <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4} bg="white" borderTopWidth="1px">
                <SimpleGrid columns={{ base: 1, md: 3 }} spacing={4} mb={4}>
                    <Box><Text fontSize="xs" color="gray.500">Valor Anterior</Text><Text fontWeight="medium">{isPriceField ? formatCurrency(log.valor_anterior) : log.valor_anterior}</Text></Box>
                    <Box><Text fontSize="xs" color="gray.500">Valor Atual</Text><Text fontWeight="bold" color="blue.600">{isPriceField ? formatCurrency(log.valor_atual) : log.valor_atual}</Text></Box>
                    <Box><Text fontSize="xs" color="gray.500">Origem</Text><Text fontWeight="medium">{log.origem_atualizacao}</Text></Box>
                </SimpleGrid>
                <Heading size="xs" mb={4} textTransform="uppercase" color="gray.600">Detalhes da Sincronização</Heading>
                <VStack spacing={4} align="stretch">
                    {log.sincronizacoes && log.sincronizacoes.length > 0 ? log.sincronizacoes.map(sinc => (<SincronizacaoItem key={sinc.id} sinc={sinc} />)) : <Text fontSize="sm" color="gray.500">Nenhuma sincronização registrada.</Text>}
                </VStack>
            </AccordionPanel>
        </AccordionItem>
    );
};

const initialFiltersState = {
    sku: '',
    codigo_global: '',
    codigo_fornecedor: '',
    campo: '',
    page: 1,
    data_inicial: '',
    data_final: ''
};

const SUPPLIERS = [
    { key: 'morelate', label: 'Morelate', logo: '/logo_morelate.webp', scheme: 'green' },
    { key: 'vannucci', label: 'Vannucci', logo: '/logo_vannucci.webp', scheme: 'blue' },
    { key: 'brcompany', label: 'BR Company', logo: '/logo_brcompany.webp', scheme: 'orange' },
    { key: 'lng', label: 'LNG', logo: '/logo_lng.webp', scheme: 'purple' },
    { key: 'cba', label: 'CBA', logo: '/logo_cba.webp', scheme: 'teal' },
    { key: 'msam', label: 'MSAM', logo: '/logo_msam.webp', scheme: 'gray' },
    { key: 'blparts', label: 'BL Parts', logo: '/logo_blparts.webp', scheme: 'cyan' },
];

const LogsProdutos = () => {
    const [supplier, setSupplier] = useState('morelate');
    const [jobStatus, setJobStatus] = useState(null);
    const [isStatusLoading, setIsStatusLoading] = useState(true);
    const [logs, setLogs] = useState([]);
    const [pagination, setPagination] = useState({});
    const [loading, setLoading] = useState(true);
    const [filters, setFilters] = useState(initialFiltersState);
    const [exportLoading, setExportLoading] = useState({ isExportingRevisao: false, isExportingResultado: false });
    const toast = useToast();
    const intervalRef = useRef(null);
    const latestLogsReq = useRef(0);

    const getFornecedor = (log) => {
        switch (log.fornecedor_id) {
            case 2: return "VANNUCCI";
            case 3: return "ISAPA";
            case 4: return "BRCOMPANY";
            case 5: return "AUTOLATINA";
            case 6: return "LNG";
            case 7: return "CBA";
            case 8: return "MSAM";
            case 9: return "BLPARTS";
            default: return "MORELATE";
        }
    };

    const formatCurrency = (value) => {
        const numberValue = parseFloat(value);
        if (isNaN(numberValue)) return value;
        return numberValue.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
    };

    const fetchLogs = useCallback(async (currentFilters) => {
        const reqId = ++latestLogsReq.current;
        setLoading(true);
        const data = await getSincronizacaoLogs(currentFilters, supplier);
        if (reqId !== latestLogsReq.current) return;
        if (data) {
            setLogs(data.data);
            setPagination({ currentPage: data.current_page, lastPage: data.last_page, total: data.total });
        } else {
            toast({ title: "Erro ao buscar logs", status: "error", duration: 5000, isClosable: true });
        }
        setLoading(false);
    }, [toast, supplier]);

    const fetchStatus = useCallback(async () => {
        try {
            const statusData = await getSincronizacaoStatus(supplier);
            if (statusData) {
                setJobStatus(prevStatus => {
                    if (prevStatus && prevStatus.status === 'running' && (statusData.status === 'completed' || statusData.status === 'failed' || statusData.status === 'stopped')) {
                        fetchLogs(filters);
                    }
                    return statusData;
                });
            }
        } catch (error) {
            console.error("Erro ao buscar status:", error);
            if (intervalRef.current) clearInterval(intervalRef.current);
            setJobStatus({ status: 'failed', message: 'Não foi possível carregar o status.' });
        } finally {
            setIsStatusLoading(false);
        }
    }, [filters, fetchLogs, supplier]);

    useEffect(() => {
        setJobStatus(null);
        setIsStatusLoading(true);
        setLogs([]);
        fetchStatus();
    }, [supplier, fetchStatus]);

    useEffect(() => {
        fetchLogs(filters);
    }, [fetchLogs, filters]);

    useEffect(() => {
        if (!jobStatus) return;

        const isMainJobRunning = jobStatus.status === 'running';
        const isExportJobRunning = ['pending', 'generating'].includes(jobStatus.revisao_export_status) ||
            ['pending', 'generating'].includes(jobStatus.resultado_export_status);

        if (isMainJobRunning || isExportJobRunning) {
            if (!intervalRef.current) {
                intervalRef.current = setInterval(fetchStatus, 5000);
            }
        } else {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        };
    }, [jobStatus, fetchStatus]);

    const handleApiAction = async (type, payload, selectedSupplier) => {
        setExportLoading(prev => ({ ...prev, [`isExporting${payload.charAt(0).toUpperCase() + payload.slice(1)}`]: true }));
        try {
            if (type === 'processo') {
                switch (payload) {
                    case 'preparar': return await prepararAtualizacao(selectedSupplier);
                    case 'executar': return await executarAtualizacao(selectedSupplier);
                    case 'parar': return await pararAtualizacao(selectedSupplier);
                    default: throw new Error('Ação de processo desconhecida');
                }
            }
            if (type === 'solicitar') {
                return payload === 'revisao' ? await solicitarExportacaoRevisao(selectedSupplier) : await solicitarExportacaoResultado(selectedSupplier);
            }
            if (type === 'baixar') {
                return await baixarArquivoExportado(payload, selectedSupplier);
            }
        } finally {
            fetchStatus();
            if (type !== 'processo') {
                setExportLoading(prev => ({ ...prev, [`isExporting${payload.charAt(0).toUpperCase() + payload.slice(1)}`]: false }));
            }
        }
    };

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters(prev => ({ ...prev, [name]: value, page: 1 }));
    };

    const handlePageChange = (newPage) => {
        setFilters(prev => ({ ...prev, page: newPage }));
    };

    const handleClearFilters = () => {
        setFilters(initialFiltersState);
    };

    return (
        <Box ml={{ base: 0, lg: '60px' }} p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <VStack spacing={8} align="stretch" maxW="7xl" mx="auto">
                <VStack spacing={4} align="start" w="100%">
                    <Heading color={"#F68633"} size="md">Logs e Controle de Sincronização de Produtos</Heading>
                    <Flex wrap="wrap" gap={2} w="100%">
                        {SUPPLIERS.map(({ key, label, logo, scheme }) => {
                            const active = supplier === key;
                            return (
                                <Button
                                    key={key}
                                    onClick={() => setSupplier(key)}
                                    variant={active ? 'solid' : 'ghost'}
                                    bg={active ? `${scheme}.100` : 'transparent'}
                                    color={active ? `${scheme}.800` : 'gray.600'}
                                    colorScheme={scheme}
                                    boxShadow={active ? 'md' : 'none'}
                                    _hover={active ? { bg: `${scheme}.200` } : { bg: 'gray.100' }}
                                    _active={active ? { bg: `${scheme}.300` } : {}}
                                    borderRadius="lg"
                                    size={{ base: 'sm', md: 'md' }}
                                    flexShrink={0}
                                    leftIcon={
                                        <Image
                                            src={logo}
                                            alt={`Logo ${label}`}
                                            h={{ base: '18px', md: '22px' }}
                                            w="auto"
                                            maxW={{ base: '48px', md: '64px' }}
                                            objectFit="contain"
                                            onError={(e) => e.target.style.display = 'none'}
                                        />
                                    }
                                >
                                    {label}
                                </Button>
                            );
                        })}
                    </Flex>
                </VStack>

                {isStatusLoading || !jobStatus ? (
                    <ControleAtualizacaoSkeleton />
                ) : (
                    <ControleAtualizacaoCard
                        jobStatus={jobStatus}
                        onAction={handleApiAction}
                        exportLoading={exportLoading}
                        supplier={supplier}
                    />
                )}

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>Filtros de Logs</Heading>
                    <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={4}>
                        <Input placeholder="SKU" name="sku" value={filters.sku} onChange={handleFilterChange} />
                        <Input placeholder="Código Fornecedor" name="codigo_fornecedor" value={filters.codigo_fornecedor} onChange={handleFilterChange} />
                        <Input placeholder="Código Global" name="codigo_global" value={filters.codigo_global} onChange={handleFilterChange} />
                        <Select placeholder="Todos os Campos" name="campo" value={filters.campo} onChange={handleFilterChange}>
                            <option value="quantidade">Estoque</option>
                            <option value="valor_venda">Preço Venda</option>
                            <option value="valor_compra">Preço Compra</option>
                            <option value="status">Status</option>
                        </Select>
                        <VStack align="start">
                            <Text fontSize="sm" fontWeight="medium" color="gray.600">Data Inicial</Text>
                            <Input
                                type="date"
                                name="data_inicial"
                                value={filters.data_inicial}
                                onChange={handleFilterChange}
                            />
                        </VStack>
                        <VStack align="start">
                            <Text fontSize="sm" fontWeight="medium" color="gray.600">Data Final</Text>
                            <Input
                                type="date"
                                name="data_final"
                                value={filters.data_final}
                                onChange={handleFilterChange}
                            />
                        </VStack>
                    </SimpleGrid>
                    <Flex justify="flex-end" mt={4}>
                        <Button
                            leftIcon={<FiXCircle />}
                            colorScheme="gray"
                            variant="outline"
                            onClick={handleClearFilters}
                        >
                            Limpar Filtros
                        </Button>
                    </Flex>
                </Box>
                <Box bg="white" borderRadius="lg" borderWidth="1px" boxShadow="sm">
                    {loading ? (<Flex justify="center" p={10}><Spinner size="xl" /></Flex>) : (
                        <Accordion allowToggle>
                            {logs.length > 0 ? logs.map(log => (
                                <LogItem
                                    key={log.log_id || log.id}
                                    log={log}
                                    getFornecedor={getFornecedor}
                                    formatCurrency={formatCurrency}
                                />
                            )) : <Text p={6} color="gray.500">Nenhum log encontrado para os filtros selecionados.</Text>}
                        </Accordion>
                    )}
                </Box>
                {pagination && pagination.total > 0 && !loading && (
                    <HStack justify="space-between" mt={4}>
                        <Text fontSize="sm" color="gray.600">Total de {pagination.total} logs</Text>
                        <HStack>
                            <Button size="sm" onClick={() => handlePageChange(pagination.currentPage - 1)} isDisabled={pagination.currentPage <= 1}>Anterior</Button>
                            <Text fontSize="sm" fontWeight="bold">Página {pagination.currentPage} de {pagination.lastPage}</Text>
                            <Button size="sm" onClick={() => handlePageChange(pagination.currentPage + 1)} isDisabled={pagination.currentPage >= pagination.lastPage}>Próxima</Button>
                        </HStack>
                    </HStack>
                )}
            </VStack>
        </Box>
    );
};

export default LogsProdutos;